/**
 * **Back into your own offline rave after signing out, with no signal** (the QA walk, w-offline-per-account,
 * 0.14.82: "after sign-out I get 'Can't reach MyRave' — a raver signed out during the rave loses the app; let them
 * sign in again to the same account even with no internet").
 *
 * Signed out is online only (`signedInHere`, the user's rule of 2026-10-27), and that rule stays: nothing is
 * served to nobody. What this adds is a way to become the SAME account again on this device without a server:
 *
 * - **What is kept.** Each time a session lands here (`AuthProvider`'s `remember`), this address keeps who it was —
 *   the id, the name to greet, the role — and a SALTED HASH of the account's email (`h1:` + SHA-256 of
 *   `<account id>:<email, lower case>`), never the address itself. Sign-out leaves it; the next account to sign
 *   in on this address replaces it. It is not a credential: it unlocks nothing on the server.
 * - **What proves it is them.** Offline, the person types the email of the account. A stranger handed the phone
 *   does not know it; the hash is checked here, with no signal. No email on the account (or an admin's account,
 *   whose every sign-in owes a second step) — nothing is offered, as before.
 * - **What it gives.** The device's sign-in marker again (`markSignedIn`) and the remembered account, so the
 *   account's OWN stored copies open (`ownStored` serves only the account that downloaded them) — exactly what it
 *   had before signing out, nothing of anyone else's.
 * - **What it does NOT give.** A session. A sign-out's revoke still goes to the server when the signal comes back
 *   (`myrave_signout_pending` is left alone), so the server-side sign-in stays ended and the app asks for a real
 *   sign-in then.
 */
import { readJson, writeJson } from './storage';
import { lastSignOutAt, markSignedIn, signedInHere } from './signOut';

/** `AuthProvider`'s remembered account — the same key, written here when the account comes back offline. */
export const SESSION_USER_KEY = 'session-user';
const REENTRY_KEY = 'offline-reentry';

export interface ReentryAccount {
  id: string;
  role: string;
  approved: boolean;
  displayName: string | null;
  nameLocal?: Record<string, string>;
}

interface ReentryRecord extends ReentryAccount {
  emailHash: string;
  at: number;
}

async function emailHash(accountId: string, email: string): Promise<string | null> {
  try {
    const bytes = new TextEncoder().encode(`${accountId}:${email.trim().toLowerCase()}`);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return `h1:${[...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return null;
  }
}

/** A session landed: keep what offline re-entry needs (never for an admin, never without an email). */
export async function noteReentry(user: ReentryAccount & { email: string | null }, secondStep = false): Promise<void> {
  if (!user.email || secondStep || user.role === 'SUPER_ADMIN') return;
  const hash = await emailHash(user.id, user.email);
  if (!hash) return;
  const { id, role, approved, displayName, nameLocal } = user;
  writeJson(null, REENTRY_KEY, { id, role, approved, displayName, nameLocal: nameLocal ?? {}, emailHash: hash, at: Date.now() } satisfies ReentryRecord);
}

function record(): ReentryRecord | null {
  const r = readJson<ReentryRecord | null>(null, REENTRY_KEY, null);
  return r && typeof r.id === 'string' && typeof r.emailHash === 'string' && r.emailHash.startsWith('h1:') ? r : null;
}

/** The account this address could take back offline: signed out since it was here, and nobody signed in now. */
export function reentryCandidate(): { id: string; name: string | null } | null {
  const r = record();
  if (!r || signedInHere()) return null;
  const out = lastSignOutAt();
  if (out === null || out < r.at) return null;
  return { id: r.id, name: r.displayName };
}

/**
 * The typed email is the account's: the device is signed in as it again, offline (see the top of this file).
 * False — and nothing changed — for anything else.
 */
export async function reenterOffline(typedEmail: string): Promise<boolean> {
  const r = record();
  if (!r || !typedEmail.trim() || !reentryCandidate()) return false;
  if ((await emailHash(r.id, typedEmail)) !== r.emailHash) return false;
  const now = Date.now();
  markSignedIn(r.id, now, false);
  writeJson(null, SESSION_USER_KEY, {
    id: r.id, role: r.role, approved: r.approved, displayName: r.displayName, nameLocal: r.nameLocal ?? {}, rememberedAt: now,
  });
  return true;
}
